import _ from "lodash";
import React, { useEffect, useState, useMemo } from "react";
import { graphql, useFragment } from "react-relay/hooks";
import Loading from "@components/atoms/Loading";
import { CampaignInputInfluencer$key } from "@generated/CampaignInputInfluencer.graphql";
import { CampaignInputCampaign$key } from "@generated/CampaignInputCampaign.graphql";
import { CampaignInputAddressPrefecture$key } from "@generated/CampaignInputAddressPrefecture.graphql";
import { CampaignInputCategoryMCategories$key } from "@generated/CampaignInputCategoryMCategories.graphql";
import checkRequirement from "@lib/util/requirementForApply";
import Address from "@components/organisms/CampaignInput/CampaignInputAddress";
import Category from "@components/organisms/CampaignInput/CampaignInputCategory";
import Profile from "@components/organisms/CampaignInput/CampaignInputProfile";
import Sns from "@components/organisms/CampaignInput/CampaignInputSns";
import EmailConfirm from "@components/organisms/CampaignInput/CampaignInputEmailConfirm";
import EmailForm from "@components/organisms/CampaignInput/CampaignInputEmailForm";
import PhoneConfirm from "@components/organisms/CampaignInput/CampaignInputPhoneConfirm";
import PhoneForm from "@components/organisms/CampaignInput/CampaignInputPhoneForm";
import CampaignInputInvoice from "@components/organisms/CampaignInput/CampaignInputInvoice";
import { goBack, replace } from "@navigation/navigate";
import { env } from "@constants/Env";
import { notify } from "@lib/util/logger";

const influecnerQuery = graphql`
  fragment CampaignInputInfluencer on Influencer {
    linkingInstagram
    linkingTwitter
    profile {
      tiktokUrl
      ytChannel
    }
    ...CampaignInputProfileInfluencer
    ...CampaignInputSnsInfluencer
    ...CampaignInputAddressInfluencer
    ...CampaignInputCategoryCategories
    ...CampaignInputEmailConfirmEmail
    ...CampaignInputInvoiceInfluencer
    ...requirementForApplyInfluencer
  }
`;

const campaignQuery = graphql`
  fragment CampaignInputCampaign on Campaign {
    id
    parentMediaType
    ...CampaignInputSnsCampaign
    ...requirementForApplyCampaign
  }
`;

export default function CampaignInput({
  influencerFragment,
  campaignFragment,
  categoryFragment,
  prefectureFragment,
}: {
  influencerFragment: CampaignInputInfluencer$key;
  campaignFragment: CampaignInputCampaign$key;
  categoryFragment: CampaignInputCategoryMCategories$key;
  prefectureFragment: CampaignInputAddressPrefecture$key;
}) {
  const influencer = useFragment<CampaignInputInfluencer$key>(
    influecnerQuery,
    influencerFragment
  );
  const campaign = useFragment<CampaignInputCampaign$key>(
    campaignQuery,
    campaignFragment
  );
  const [token, setToken] = useState<string | null>(null);
  const [phone, setPhone] = useState<string | null>(null);

  const requiredErrors = checkRequirement(influencer, campaign);

  // コンポーネントマウント時のログ
  useEffect(() => {
    notify(
      {
        component: "CampaignInput",
        event: "mount",
        campaignId: campaign.id,
        parentMediaType: campaign.parentMediaType,
        requiredErrors: requiredErrors?.map((e) => e.code) ?? [],
        requiredErrorsCount: requiredErrors?.length ?? 0,
      },
      { level: "monitor" }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // requiredErrorsの変化をログ
  useEffect(() => {
    notify(
      {
        component: "CampaignInput",
        event: "requirementCheck",
        campaignId: campaign.id,
        requiredErrors: requiredErrors?.map((e) => e.code) ?? [],
        requiredErrorsCount: requiredErrors?.length ?? 0,
        willNavigateToApply: requiredErrors === null,
      },
      { level: "monitor" }
    );

    if (requiredErrors === null) {
      notify(
        {
          component: "CampaignInput",
          event: "navigateToApply",
          campaignId: campaign.id,
        },
        { level: "monitor" }
      );
      replace("CampaignApply", { id: campaign.id });
    }
  }, [requiredErrors, campaign]);

  const requiredCategory = useMemo<boolean>(() => {
    const error = _.head(
      requiredErrors?.filter((row) => row.code === "CategoryRequired")
    );
    return !_.isNil(error);
  }, [requiredErrors]);

  const requiredEmail = useMemo<boolean>(() => {
    const error = _.head(
      requiredErrors?.filter((row) => row.code === "EmailRequired")
    );
    return !_.isNil(error);
  }, [requiredErrors]);

  const requiredPhone = useMemo<boolean>(() => {
    const error = _.head(
      requiredErrors?.filter((row) => row.code === "PhoneRequired")
    );
    return !_.isNil(error);
  }, [requiredErrors]);

  const requiredAddress = useMemo<boolean>(() => {
    const error = _.head(
      requiredErrors?.filter((row) => row.code === "AddressRequired")
    );
    return !_.isNil(error);
  }, [requiredErrors]);

  const requiredProfile = useMemo<boolean>(() => {
    const error = _.head(
      requiredErrors?.filter((row) => row.code === "ProfileRequired")
    );
    return !_.isNil(error);
  }, [requiredErrors]);

  const requiredSns = useMemo<boolean>(() => {
    switch (campaign.parentMediaType) {
      case "instagram":
        // Skip Instagram validation for staging, development, and local environments
        if (env === "staging" || env === "development" || env === "local") {
          return false;
        }
        return influencer.linkingInstagram !== true;
      case "twitter":
        return influencer.linkingTwitter !== true;
      case "youtube":
        return _.isEmpty(influencer.profile.ytChannel);
      case "tiktok":
      case "tiktok_go":
        return _.isEmpty(influencer.profile.tiktokUrl);
      default:
        return false;
    }
  }, [campaign, influencer]);

  const requiredInvoice = useMemo<boolean>(() => {
    const error = _.head(
      requiredErrors?.filter((row) => row.code === "InvoiceRequired")
    );
    return !_.isNil(error);
  }, [requiredErrors]);

  // 現在表示される画面を判定してログ
  const currentScreen = useMemo(() => {
    if (requiredProfile) return "Profile";
    if (requiredSns) return "Sns";
    if (requiredEmail) return token !== null ? "EmailConfirm" : "EmailForm";
    if (requiredPhone) return phone !== null ? "PhoneConfirm" : "PhoneForm";
    if (requiredCategory) return "Category";
    if (requiredAddress) return "Address";
    if (requiredInvoice) return "Invoice";
    return "Loading";
  }, [
    requiredProfile,
    requiredSns,
    requiredEmail,
    requiredPhone,
    requiredCategory,
    requiredAddress,
    requiredInvoice,
    token,
    phone,
  ]);

  useEffect(() => {
    notify(
      {
        component: "CampaignInput",
        event: "screenDisplay",
        campaignId: campaign.id,
        currentScreen,
        requirements: {
          requiredProfile,
          requiredSns,
          requiredEmail,
          requiredPhone,
          requiredCategory,
          requiredAddress,
          requiredInvoice,
        },
      },
      { level: "monitor" }
    );
  }, [
    campaign.id,
    currentScreen,
    requiredProfile,
    requiredSns,
    requiredEmail,
    requiredPhone,
    requiredCategory,
    requiredAddress,
    requiredInvoice,
  ]);

  if (requiredProfile) {
    return (
      <Profile
        hasNext={requiredErrors !== null ? requiredErrors.length > 1 : false}
        onClose={goBack}
        profileFragment={influencer}
      />
    );
  }
  if (requiredSns) {
    return (
      <Sns
        campaignFragment={campaign}
        hasNext={requiredErrors !== null ? requiredErrors.length > 1 : false}
        onClose={goBack}
        snsFragment={influencer}
      />
    );
  }
  if (requiredEmail) {
    if (token !== null) {
      return (
        <EmailConfirm
          emailFragment={influencer}
          hasNext={requiredErrors !== null ? requiredErrors.length > 1 : false}
          initialToken={token}
          onClose={() => setToken(null)}
        />
      );
    }
    return <EmailForm onClose={goBack} onComplete={setToken} />;
  }

  if (requiredPhone) {
    if (phone !== null) {
      return (
        <PhoneConfirm
          hasNext={requiredErrors !== null ? requiredErrors.length > 1 : false}
          onClose={() => setPhone(null)}
          phone={phone}
        />
      );
    }
    return <PhoneForm onClose={goBack} onComplete={setPhone} />;
  }

  if (requiredCategory) {
    return (
      <Category
        categoryFragment={influencer}
        hasNext={requiredErrors !== null ? requiredErrors.length > 1 : false}
        listFragment={categoryFragment}
        onClose={goBack}
      />
    );
  }
  if (requiredAddress) {
    return (
      <Address
        addressFragment={influencer}
        hasNext={requiredErrors !== null ? requiredErrors.length > 1 : false}
        onClose={goBack}
        prefecturesFragment={prefectureFragment}
      />
    );
  }
  if (requiredInvoice) {
    return (
      <CampaignInputInvoice
        hasNext={requiredErrors !== null ? requiredErrors.length > 1 : false}
        onClose={goBack}
        profileFragment={influencer}
      />
    );
  }
  return <Loading mask />;
}
